import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Truck, Shield, ArrowLeft, Package, RefreshCcw, Search, User, ShoppingBag, Menu, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProduct } from '@/hooks/useProducts';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import CartDrawer from '@/components/CartDrawer';
import { toast } from 'sonner';

const ProductPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToCart, setIsCartOpen, getCartCount } = useCart();
  const { currentUser, logout } = useAuth();
  const { product, loading, error } = useProduct(id);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [clickTimer, setClickTimer] = useState(null);
  const cartCount = getCartCount();

  // No login required to view products - removed authentication requirement

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleTripleClick = () => {
    setClickCount((prev) => prev + 1);
    if (clickTimer) clearTimeout(clickTimer);
    const timer = setTimeout(() => setClickCount(0), 1000);
    setClickTimer(timer);
    if (clickCount + 1 === 3) {
      navigate('/admin/login');
      setClickCount(0);
    }
  };

  const handleCartClick = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setIsCartOpen(true);
  };

  // Parse colors from product
  const getColors = () => {
    if (!product) return [];
    if (product.colors) {
      if (typeof product.colors === 'string') {
        return product.colors.split(',').map(c => c.trim()).filter(c => c);
      }
      return product.colors;
    }
    return [];
  };

  const colors = getColors();

  useEffect(() => {
    if (colors.length > 0 && !selectedColor) {
      setSelectedColor(colors[0]);
    }
  }, [product, colors]);

  // Calculate discount percentage
  const getDiscountPercentage = () => {
    const originalPrice = product?.originalPrice || product?.price;
    if (!product?.discountPrice || product.discountPrice >= originalPrice) return 0;
    return Math.round(((originalPrice - product.discountPrice) / originalPrice) * 100);
  };

  const discountPercent = getDiscountPercentage();

  const handleAddToCart = () => {
    if (!product) return;
    
    // Require login to add to cart
    if (!currentUser) {
      toast.info('Please login to add items to cart');
      navigate('/login');
      return;
    }
    
    const variant = {
      id: selectedColor || 'default',
      color: selectedColor || 'Default',
      image: product.images?.[0] || 'https://via.placeholder.com/200',
      stock: 10,
    };
    
    addToCart(product, variant, 1);
    toast.success('Added to cart!', {
      description: `${product.title}${selectedColor ? ` - ${selectedColor}` : ''}`,
      action: {
        label: 'View Cart',
        onClick: () => setIsCartOpen(true),
      },
    });
  };

  // Get color hex value
  const getColorHex = (colorName) => {
    const colorMap = {
      'black': '#000000',
      'white': '#FFFFFF',
      'red': '#EF4444',
      'blue': '#3B82F6',
      'green': '#22C55E',
      'gold': '#D4AF37',
      'silver': '#C0C0C0',
      'pink': '#EC4899',
      'purple': '#A855F7',
      'orange': '#F97316',
      'gray': '#6B7280',
      'grey': '#6B7280',
      'navy': '#1E3A5F',
      'brown': '#92400E',
      'yellow': '#EAB308',
      'cyan': '#06B6D4',
      'transparent': 'transparent',
      'clear': 'transparent',
    };
    return colorMap[colorName.toLowerCase()] || colorName;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 font-light">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl mb-4 font-light">Product not found</p>
          <button 
            onClick={() => navigate('/products')}
            className="px-6 py-3 bg-black text-white text-sm font-light tracking-wide"
          >
            View All Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <CartDrawer />

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="hidden md:flex items-center space-x-8 w-32">
              {/* Removed Catalog and Contact */}
            </div>

            <button className="md:hidden text-black" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Center - Logo - Click to go home, Triple click to go to admin */}
            <div className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer" onClick={handleLogoClick} onDoubleClick={handleTripleClick}>
              <img 
                src="https://customer-assets.emergentagent.com/job_luxury-estore-3/artifacts/czobo02e_B%26C%20LOGO%20-%2001.png" 
                alt="B&C Store" 
                className="h-10 sm:h-12 object-contain hover:opacity-80 transition-opacity"
              />
            </div>

            <div className="flex items-center space-x-4 sm:space-x-5">
              {currentUser ? (
                <div className="relative">
                  <button onClick={() => setShowUserMenu(!showUserMenu)} className="text-gray-600 hover:text-black transition-colors">
                    <User size={20} strokeWidth={1.5} />
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-100">
                      <button onClick={() => { navigate('/profile'); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light">My Profile</button>
                      <button onClick={() => { navigate('/my-orders'); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light">My Orders</button>
                      <button onClick={async () => { await logout(); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 font-light">Logout</button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => navigate('/login')} className="text-gray-600 hover:text-black transition-colors">
                  <User size={20} strokeWidth={1.5} />
                </button>
              )}
              
              <button onClick={handleCartClick} className="text-gray-600 hover:text-black transition-colors relative">
                <ShoppingBag size={20} strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-light">{cartCount}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">Home</button>
              <button onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">My Profile</button>
              <button onClick={() => { navigate('/my-orders'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">My Orders</button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content */}
      <div className="pt-20 sm:pt-24 pb-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/products')}
            className="flex items-center space-x-2 text-gray-500 hover:text-black mb-6 sm:mb-8 transition-colors font-light text-sm"
            data-testid="back-button"
          >
            <ArrowLeft size={18} strokeWidth={1.5} />
            <span>Back to Products</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Product Images */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              {/* Main Image */}
              <div className="aspect-square bg-[#F5F5F5] overflow-hidden">
                <img
                  src={product.images?.[selectedImage] || product.images?.[0] || 'https://via.placeholder.com/600'}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Thumbnail Images */}
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {product.images.slice(0, 4).map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`aspect-square bg-[#F5F5F5] overflow-hidden border-2 transition-colors ${
                        selectedImage === idx ? 'border-black' : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      <img src={img} alt={`${product.title} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Product Details */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Category & Model */}
              <div className="flex items-center gap-3 flex-wrap">
                {product.category && (
                  <span className="text-xs uppercase tracking-wider text-gray-500 font-light">
                    {product.category}
                  </span>
                )}
                {product.modelName && (
                  <>
                    <span className="text-gray-300">•</span>
                    <span className="text-xs text-gray-500 font-light">
                      📱 {product.modelName}
                    </span>
                  </>
                )}
              </div>

              {/* Product Name */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-wide" data-testid="product-title">
                {product.title}
              </h1>

              {/* Price Section */}
              <div className="space-y-2">
                <div className="flex items-center gap-4 flex-wrap">
                  {product.discountPrice && product.discountPrice < (product.originalPrice || product.price) ? (
                    <>
                      <span className="text-xl text-gray-400 line-through font-light">₹{product.originalPrice || product.price}</span>
                      <span className="text-3xl sm:text-4xl font-medium">₹{product.discountPrice}</span>
                      <span className="bg-black text-white text-xs px-3 py-1 font-light tracking-wide">
                        {discountPercent}% OFF
                      </span>
                    </>
                  ) : (
                    <span className="text-3xl sm:text-4xl font-medium" data-testid="product-price">₹{product.originalPrice || product.price}</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 font-light">Inclusive of all taxes</p>
              </div>

              {/* Color Selection */}
              {colors.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-light tracking-wide">Colors</h3>
                    {selectedColor && (
                      <span className="text-sm text-gray-500 font-light">{selectedColor}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {colors.map((color, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedColor(color)}
                        className={`relative group flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all ${
                          selectedColor === color 
                            ? 'border-black scale-110' 
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        title={color}
                        data-testid={`color-${color.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <div 
                          className="w-9 h-9 rounded-full"
                          style={{ 
                            backgroundColor: getColorHex(color),
                            border: color.toLowerCase() === 'white' ? '1px solid #e5e5e5' : 'none'
                          }}
                        />
                        {selectedColor === color && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-black rounded-full flex items-center justify-center">
                            <Check size={12} className="text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {product.description && (
                <div className="py-4 border-t border-gray-100">
                  <p className="text-gray-600 leading-relaxed text-sm font-light" data-testid="product-description">
                    {product.description}
                  </p>
                </div>
              )}

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                className="w-full py-4 bg-black text-white text-sm font-light tracking-widest hover:bg-gray-900 transition-colors"
                data-testid="add-to-cart-button"
              >
                ADD TO CART — ₹{product.discountPrice || product.originalPrice || product.price}
              </button>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="flex items-center space-x-3 p-4 bg-white border border-gray-100">
                  <Truck size={20} strokeWidth={1.5} className="text-gray-600" />
                  <div>
                    <p className="text-xs font-light text-gray-900">Free Delivery</p>
                    <p className="text-[10px] text-gray-500 font-light">2-3 working days</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white border border-gray-100">
                  <Shield size={20} strokeWidth={1.5} className="text-gray-600" />
                  <div>
                    <p className="text-xs font-light text-gray-900">Secure Payment</p>
                    <p className="text-[10px] text-gray-500 font-light">COD available</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white border border-gray-100">
                  <RefreshCcw size={20} strokeWidth={1.5} className="text-gray-600" />
                  <div>
                    <p className="text-xs font-light text-gray-900">Easy Returns</p>
                    <p className="text-[10px] text-gray-500 font-light">7-day policy</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white border border-gray-100">
                  <Package size={20} strokeWidth={1.5} className="text-gray-600" />
                  <div>
                    <p className="text-xs font-light text-gray-900">Premium Pack</p>
                    <p className="text-[10px] text-gray-500 font-light">Safe delivery</p>
                  </div>
                </div>
              </div>

              {/* Product Details Table */}
              <div className="pt-6 border-t border-gray-100">
                <h3 className="text-sm font-light tracking-wide mb-4">Product Details</h3>
                <div className="space-y-3 text-sm">
                  {product.category && (
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-gray-500 font-light">Category</span>
                      <span className="font-light">{product.category}</span>
                    </div>
                  )}
                  {product.modelName && (
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-gray-500 font-light">Phone Model</span>
                      <span className="font-light">{product.modelName}</span>
                    </div>
                  )}
                  {colors.length > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-gray-500 font-light">Available Colors</span>
                      <span className="font-light">{colors.join(', ')}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-500 font-light">Material</span>
                    <span className="font-light">Premium Quality</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500 font-light">Warranty</span>
                    <span className="font-light">6 Months</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
